import { Link, Pencil, Unlink } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FlatfinderEstate, FlatfinderRegion } from 'realitiq-utils/src/flatfinder';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export function FlatfinderApartmentList({
  estates,
  regions,
  busy,
  canAssign,
  selectedId,
  canConnect,
  onHover,
  hoveredEstateId,
  onEdit,
  onSelect,
  onConnect,
  onDisconnect,
  onStartConnect,
}: {
  estates: FlatfinderEstate[];
  regions: FlatfinderRegion[];
  busy: boolean;
  canAssign: boolean;
  selectedId: string | null;
  canConnect: boolean;
  onHover: (id: string | null) => void;
  hoveredEstateId: string | null;
  onEdit: (id: string) => void;
  onSelect: (id: string) => void;
  onConnect: (id: string) => void;
  onDisconnect: (id: string) => void;
  onStartConnect: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  useEffect(() => () => onHover(null), [onHover]);
  const selectedEstateId = regions.find((region) => region.id === selectedId)?.estateId;
  const activeFilter = canConnect ? 'Unassigned' : selectedEstateId ? 'Assigned' : filter;
  const apartments = estates.filter((estate) => {
    if (selectedEstateId) return estate.id === selectedEstateId;
    const status = regions.some((region) => region.estateId === estate.id)
      ? 'Assigned'
      : 'Unassigned';
    return (
      (activeFilter === 'All' || activeFilter === status) &&
      `${estate.name} ${estate.topNumber ?? ''}`
        .toLocaleLowerCase()
        .includes(search.toLocaleLowerCase())
    );
  });
  return (
    <>
      <Input
        aria-label={t('searchApartments')}
        type="search"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          onHover(null);
        }}
        placeholder={t('apartmentSearchPlaceholder')}
      />
      <fieldset
        className="grid grid-cols-3 divide-x overflow-hidden rounded-lg border"
        aria-label={t('assignmentFilter')}
      >
        {(
          [
            { status: 'All', label: 'all' },
            { status: 'Assigned', label: 'assigned' },
            { status: 'Unassigned', label: 'unassigned' },
          ] as const
        ).map(({ status, label }) => (
          <Button
            key={status}
            size="sm"
            variant="ghost"
            className={
              activeFilter === status
                ? 'whitespace-normal rounded-none border-0 bg-primary/10 px-1 text-xs font-semibold text-primary hover:bg-primary/15 lg:px-1'
                : 'whitespace-normal rounded-none border-0 px-1 text-xs text-text-secondary lg:px-1'
            }
            aria-pressed={activeFilter === status}
            disabled={canConnect || Boolean(selectedEstateId)}
            onClick={() => {
              setFilter(status);
              onHover(null);
            }}
          >
            {t(label)}
          </Button>
        ))}
      </fieldset>
      <ul className="flex flex-col gap-2">
        {apartments.map((apartment) => {
          const assigned = regions.filter((region) => region.estateId === apartment.id);
          if (canConnect)
            return (
              <li key={apartment.id}>
                <Button
                  variant="ghost"
                  className="min-h-12 w-full justify-start gap-2 border border-primary-dark/20 px-2 lg:px-2"
                  disabled={busy || !canAssign}
                  onClick={() => onConnect(apartment.id)}
                  title={apartment.name}
                >
                  <span className="w-20 truncate text-left">{apartment.name}</span>
                  <span className="rounded-sm bg-yellow-200 px-1.5 py-0.5 text-xs text-yellow-900">
                    {t('unassigned')}
                  </span>
                </Button>
              </li>
            );
          return (
            <li
              key={apartment.id}
              className={`flex items-center gap-1 rounded-sm border p-1 hover:bg-primary/5 ${hoveredEstateId === apartment.id ? 'bg-primary/5' : ''}`}
              onMouseEnter={() => onHover(apartment.id)}
              onMouseLeave={() => onHover(null)}
              onFocus={() => onHover(apartment.id)}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) onHover(null);
              }}
            >
              <div className="flex min-w-0 items-center gap-1">
                <Button
                  variant="ghost"
                  className="h-auto w-20 min-w-0 shrink-0 justify-start px-1 py-1 lg:px-1"
                  title={apartment.name}
                  aria-pressed={assigned.some((region) => region.id === selectedId)}
                  disabled={busy || !assigned.length}
                  onClick={() => {
                    if (assigned[0]) onSelect(assigned[0].id);
                  }}
                >
                  <span className="truncate">{apartment.name}</span>
                </Button>
                {assigned.length > 0 && (
                  <span className="flex -space-x-1">
                    {[...new Set(assigned.map((region) => region.color))].map((color) => (
                      <span
                        key={color}
                        className="size-3 shrink-0 rounded-full border border-white"
                        style={{ backgroundColor: color }}
                        title={t('areaColor', { color: color })}
                      />
                    ))}
                  </span>
                )}
                <span
                  className={
                    assigned.length
                      ? 'shrink-0 rounded-sm bg-green-100 px-1.5 py-0.5 text-xs text-green-800'
                      : 'shrink-0 rounded-sm bg-yellow-200 px-1.5 py-0.5 text-xs text-yellow-900'
                  }
                >
                  {assigned.length ? t('assigned') : t('unassigned')}
                </span>
              </div>
              <div className="ml-auto flex shrink-0 items-center">
                <Button
                  variant="ghost"
                  className="size-9 shrink-0 p-2 lg:px-2 [&_svg]:size-4"
                  aria-label={t('flatfinderControlEditApartment', { name: apartment.name })}
                  title={t('edit')}
                  disabled={busy || !apartment.canEdit}
                  onClick={() => onEdit(apartment.id)}
                >
                  <Pencil />
                </Button>
                {assigned.length > 0 ? (
                  <Button
                    variant="ghost"
                    className="size-9 shrink-0 p-2 lg:px-2 [&_svg]:size-4"
                    aria-label={t('disconnectApartment', {
                      name: apartment.name,
                    })}
                    title={t('disconnect')}
                    disabled={busy || !canAssign}
                    onClick={() => onDisconnect(apartment.id)}
                  >
                    <Unlink />
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    className="size-9 shrink-0 p-2 lg:px-2 [&_svg]:size-4"
                    aria-label={t('connectApartment', { name: apartment.name })}
                    title={t('connect')}
                    disabled={busy || !canAssign || !regions.some((region) => !region.estateId)}
                    onClick={() => onStartConnect(apartment.id)}
                  >
                    <Link />
                  </Button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {!apartments.length && (
        <p className="text-sm text-text-secondary">{t('flatfinderControlNoApartments')}</p>
      )}
    </>
  );
}
